import React, { useRef, useState, useEffect } from "react";
import Axios from "axios";
import { toast, ToastContainer } from "react-toastify";

const ALLOWED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];
const MAX_IMAGE_SIZE = 500 * 1024; // 500 KB
const MIN_REVIEW_LENGTH = 40;
const MAX_REVIEW_LENGTH = 600;

const ManageReviews = () => {
  const [showModal, setShowModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  // ── Form fields (match `user_reviews` / `user_review_requests` columns) ──
  const [reviewerName, setReviewerName] = useState("");
  const [rating, setRating] = useState(5);
  //   const [reviewDate, setReviewDate] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [reviewerImage, setReviewerImage] = useState(null); // new file to upload
  const [currentImage, setCurrentImage] = useState(""); // existing filename when editing

  const [editingId, setEditingId] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [errors, setErrors] = useState({});

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  // ── Drag & drop reordering state ──────────────────────────
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  // show description modal
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReviewText, setSelectedReviewText] = useState("");

  const openReviewModal = (text) => {
    setSelectedReviewText(text);
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedReviewText("");
  };

  const selectedReview =
    (reviews || []).find((review) => review.id === editingId) || null;

  useEffect(() => {
    displayReviews();
    loadPendingReviews();
  }, []);

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!reviewerName.trim()) {
      newErrors.reviewerName = "Reviewer name is required.";
    } else if (reviewerName.trim().length < 2) {
      newErrors.reviewerName = "Reviewer name must be at least 2 characters.";
    }

    const ratingNum = Number(rating);
    if (!ratingNum || ratingNum < 1 || ratingNum > 5) {
      newErrors.rating = "Rating must be between 1 and 5.";
    }

    // if (!reviewDate) {
    //   newErrors.reviewDate = "Review date is required.";
    // }

    if (!reviewText.trim()) {
      newErrors.reviewText = "Please enter the review text.";
    } else if (reviewText.trim().length < MIN_REVIEW_LENGTH) {
      newErrors.reviewText = `Review must be at least ${MIN_REVIEW_LENGTH} characters.`;
    } else if (reviewText.length > MAX_REVIEW_LENGTH) {
      newErrors.reviewText = `Review cannot exceed ${MAX_REVIEW_LENGTH} characters.`;
    }

    // An image is only required when adding, or when editing without an
    // existing image already on file. Editing can keep the current image.
    const hasExistingImage = Boolean(editingId && currentImage);

    if (!reviewerImage && !hasExistingImage) {
      newErrors.reviewerImage = "Please choose a reviewer image.";
    } else if (reviewerImage) {
      if (!ALLOWED_IMAGE_TYPES.includes(reviewerImage.type)) {
        newErrors.reviewerImage =
          "Only JPG, JPEG, PNG & WEBP files are allowed.";
      } else if (reviewerImage.size > MAX_IMAGE_SIZE) {
        newErrors.reviewerImage = "Image size must be less than 500 KB.";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const uploadReview = async () => {
    if (!validateForm()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("reviewer_name", reviewerName);
      formData.append("rating", rating);
      //   formData.append("review_date", reviewDate);
      formData.append("review_text", reviewText);
      formData.append("reviewer_image", reviewerImage);

      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/add_review.php`,
        formData,
      );

      if (res.data.success) {
        toast.success("Review added successfully.");
        clearForm();
        setShowModal(false);
        displayReviews();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.log(err);
      toast.error("Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const displayReviews = async () => {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_BASE_URL}php/get_reviews.php`,
      );
      console.log(res.data);

      if (res.data.success) {
        setReviews(Array.isArray(res.data.reviews) ? res.data.reviews : []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const editReview = (review) => {
    setEditingId(review.id);

    setReviewerName(review.reviewer_name);
    setRating(review.rating);
    // setReviewDate(review.review_date ? review.review_date.slice(0, 10) : "");
    setReviewText(review.review_text);
    setReviewerImage(null);
    setCurrentImage(review.reviewer_image);

    const fileInput = document.getElementById("reviewerImage");
    if (fileInput) {
      fileInput.value = "";
    }

    setErrors({});

    formRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const clearForm = () => {
    setEditingId(null);

    setReviewerName("");
    setRating(5);
    // setReviewDate("");
    setReviewText("");
    setReviewerImage(null);
    setCurrentImage("");
    setErrors({});

    const fileInput = document.getElementById("reviewerImage");

    if (fileInput) {
      fileInput.value = "";
    }
  };

  const updateReview = async () => {
    if (!validateForm()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("id", editingId);
      formData.append("reviewer_name", reviewerName);
      formData.append("rating", rating);
      //   formData.append("review_date", reviewDate);
      formData.append("review_text", reviewText);

      if (reviewerImage) {
        formData.append("reviewer_image", reviewerImage);
      }

      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/update_review.php`,
        formData,
      );

      if (res.data.success) {
        toast.success("Review updated successfully.");
        clearForm();
        setShowModal(false);
        displayReviews();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.log(err);
      toast.error("Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;

    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/delete_review.php`,
        { id },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data.success) {
        toast.success("Review deleted successfully.");
        displayReviews();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.log(err);
      toast.error("Delete failed.");
    }
  };

  const loadPendingReviews = async () => {
    const res = await Axios.get(
      `${process.env.REACT_APP_API_BASE_URL}php/get_pending_reviews.php`,
    );

    if (res.data.success) {
      setPendingReviews(res.data.reviews);
    }
  };

  const approveReview = async (id) => {
    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/approve_review.php`,
        { id },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data.success) {
        toast.success("Review approved.");

        loadPendingReviews();
        displayReviews();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const rejectReview = async (id) => {
    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/reject_review.php`,
        { id },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data.success) {
        toast.success("Review rejected.");

        loadPendingReviews();
        displayReviews();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/review-testimonial`;

    navigator.clipboard.writeText(url);

    toast.success("Form link copied!");
  };

  // ── Modal + read-more helpers ─────────────────────────────
  const openAddModal = () => {
    clearForm();
    setShowModal(true);
  };

  const openEditModal = (review) => {
    editReview(review);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    clearForm();
  };

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderStars = (value) => {
    const num = Number(value) || 0;
    return (
      <span style={{ color: "#f5a623", whiteSpace: "nowrap" }}>
        {"★".repeat(num)}
        <span style={{ color: "#dcdfe3" }}>{"★".repeat(5 - num)}</span>
      </span>
    );
  };

  // ── Drag & drop reordering handlers ───────────────────────
  const handleDragStart = (e, index) => {
    if (savingOrder) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData to be called for drag to initiate
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (index !== dragOverIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const reordered = [...reviews];
    const [movedReview] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, movedReview);

    setReviews(reordered);
    setDraggedIndex(null);
    setDragOverIndex(null);

    saveOrder(reordered);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const saveOrder = async (orderedReviews) => {
    try {
      setSavingOrder(true);

      const order = orderedReviews.map((review, index) => ({
        id: review.id,
        sort_order: index,
      }));

      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/reorder_reviews.php`,
        { order },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data.success) {
        toast.success("Order updated.");
      } else {
        toast.error(res.data.message || "Failed to save order.");
        displayReviews(); // revert to last saved order on failure
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to save order.");
      displayReviews(); // revert to last saved order on failure
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <>
      <div className="twm-right-section-panel site-bg-gray">
        <div className="panel panel-default">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-4">
            <h4 className="mb-0">Manage Reviews</h4>

            <div className="d-flex gap-2">
              <button className="site-button px-4 py-2" onClick={copyLink}>
                <i className="fa fa-copy me-1"></i>
                Copy Form Link
              </button>

              <button className="site-button px-4 py-2" onClick={openAddModal}>
                <i className="fa fa-plus me-1"></i>
                Add Review
              </button>
            </div>
          </div>

          <div className="panel-body p-a30">
            {/* ================= PENDING SECTION (user_review_requests) ================= */}
            {pendingReviews.length > 0 && (
              <section className="pending-reviews-section">
                <div
                  className="card mb-4"
                  style={{
                    border: "1px solid #f5c76b",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    className="d-flex justify-content-between align-items-center px-3 py-2"
                    style={{
                      backgroundColor: "#fff6e6",
                      borderBottom: "1px solid #f5c76b",
                    }}
                  >
                    <h5 className="mb-0" style={{ color: "#8a5a00" }}>
                      <i className="fa fa-clock-o me-2"></i>
                      Pending Review Requests
                    </h5>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: "#f5a623",
                        color: "#fff",
                        fontSize: "0.85rem",
                      }}
                    >
                      {pendingReviews.length}
                    </span>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead style={{ backgroundColor: "#fdf1dc" }}>
                        <tr>
                          <th>Date</th>
                          <th style={{ width: "70px" }}>Image</th>
                          <th>Reviewer</th>
                          <th>Rating</th>
                          <th style={{ minWidth: "260px" }}>Review</th>
                          {/* <th>Submitted</th> */}
                          <th
                            style={{ width: "100px" }}
                            className="text-center"
                          >
                            Action
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {pendingReviews.map((review) => {
                          const isExpanded = !!expandedRows[review.id];
                          return (
                            <tr key={review.id}>
                              <td>
                                {review.review_date
                                  ? new Date(
                                      review.review_date,
                                    ).toLocaleDateString()
                                  : "-"}
                              </td>
                              <td>
                                <img
                                  src={`${process.env.REACT_APP_API_BASE_URL}php/uploads/reviews/${review.reviewer_image}`}
                                  alt={review.reviewer_name}
                                  width="50"
                                  height="50"
                                  style={{
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                  }}
                                />
                              </td>

                              <td
                                className="text-wrap"
                                style={{ maxWidth: "150px" }}
                              >
                                <strong>{review.reviewer_name}</strong>
                              </td>

                              <td>{renderStars(review.rating)}</td>

                              <td style={{ maxWidth: "320px" }}>
                                <p
                                  className="mb-1"
                                  style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    margin: 0,
                                  }}
                                >
                                  {review.review_text}
                                </p>
                                {review.review_text &&
                                  review.review_text.length > 120 && (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0"
                                      onClick={() =>
                                        openReviewModal(review.review_text)
                                      }
                                    >
                                      Read more
                                    </button>
                                  )}
                              </td>

                              {/* <td>
                                {review.submitted_at
                                  ? new Date(
                                      review.submitted_at,
                                    ).toLocaleString()
                                  : "-"}
                              </td> */}

                              <td>
                                <div className="d-flex justify-content-center gap-2">
                                  <button
                                    className="btn btn-success btn-sm"
                                    title="Approve"
                                    onClick={() => approveReview(review.id)}
                                  >
                                    <i className="fa fa-check"></i>
                                    <span className="visually-hidden">
                                      Approve
                                    </span>
                                  </button>

                                  <button
                                    className="btn btn-danger btn-sm"
                                    title="Reject"
                                    onClick={() => rejectReview(review.id)}
                                  >
                                    <i className="fa fa-times"></i>
                                    <span className="visually-hidden">
                                      Reject
                                    </span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {/* ================= APPROVED SECTION (user_reviews) ================= */}
            <div
              className="card"
              style={{
                border: "1px solid #dcdfe3",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center px-3 py-2"
                style={{
                  backgroundColor: "#f4f6f8",
                  borderBottom: "1px solid #dcdfe3",
                }}
              >
                <h5 className="mb-0">
                  <i
                    className="fa fa-check-circle me-2"
                    style={{ color: "#2ecc71" }}
                  ></i>
                  Published Reviews
                </h5>
                <div className="d-flex align-items-center gap-2">
                  {savingOrder && (
                    <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                      Saving order...
                    </span>
                  )}
                  <span
                    className="badge"
                    style={{
                      backgroundColor: "#4a90e2",
                      color: "#fff",
                      fontSize: "0.85rem",
                    }}
                  >
                    {reviews.length}
                  </span>
                </div>
              </div>

              {/* {reviews.length > 1 && (
                <div
                  className="px-3 py-2 text-muted"
                  style={{ fontSize: "0.82rem", backgroundColor: "#fbfcfd" }}
                >
                  <i className="fa fa-arrows me-1"></i>
                  Drag rows using the handle to reorder how reviews appear on
                  the site.
                </div>
              )} */}

              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead style={{ backgroundColor: "#eef1f4" }}>
                    <tr>
                      {/* <th style={{ width: "40px" }}></th> */}
                      <th>Date</th>
                      <th style={{ width: "70px" }}>Image</th>
                      <th>Reviewer</th>
                      <th>Rating</th>
                      <th style={{ minWidth: "260px" }}>Review</th>
                      {/* <th>Status</th> */}
                      <th style={{ width: "90px" }} className="text-center">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {reviews.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center text-muted py-4">
                          No reviews found.
                        </td>
                      </tr>
                    ) : (
                      reviews.map((review, index) => {
                        const isExpanded = !!expandedRows[review.id];
                        const isDragging = draggedIndex === index;
                        const isDragOver =
                          dragOverIndex === index && draggedIndex !== index;

                        return (
                          <tr
                            key={review.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            style={{
                              opacity: isDragging ? 0.4 : 1,
                              borderTop: isDragOver
                                ? "2px solid #4a90e2"
                                : undefined,
                              backgroundColor: isDragOver
                                ? "#f0f7ff"
                                : undefined,
                            }}
                          >
                            {/* <td
                              style={{
                                cursor: savingOrder ? "wait" : "grab",
                                textAlign: "center",
                                color: "#9aa5b1",
                              }}
                              title="Drag to reorder"
                            >
                              <i className="fa fa-arrows"></i>
                            </td> */}
                            <td>
                              {review.review_date
                                ? new Date(
                                    review.review_date,
                                  ).toLocaleDateString()
                                : "-"}
                            </td>
                            <td>
                              <img
                                src={`${process.env.REACT_APP_API_BASE_URL}php/uploads/reviews/${review.reviewer_image}`}
                                alt={review.reviewer_name}
                                width="50"
                                height="50"
                                style={{
                                  borderRadius: "8px",
                                  objectFit: "cover",
                                }}
                              />
                            </td>

                            <td
                              className="text-wrap"
                              style={{ maxWidth: "150px" }}
                            >
                              {review.reviewer_name}
                            </td>
                            <td>{renderStars(review.rating)}</td>

                            <td style={{ maxWidth: "320px" }}>
                              <p
                                className="mb-1"
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  margin: 0,
                                }}
                              >
                                {review.review_text}
                              </p>
                              {review.review_text &&
                                review.review_text.length > 120 && (
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0"
                                    onClick={() =>
                                      openReviewModal(review.review_text)
                                    }
                                  >
                                    Read more
                                  </button>
                                )}
                            </td>

                            {/* <td>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor:
                                    review.status === "Approved"
                                      ? "#2ecc71"
                                      : "#9aa5b1",
                                  color: "#fff",
                                  textTransform: "capitalize",
                                }}
                              >
                                {review.status}
                              </span>
                            </td> */}

                            <td>
                              <div className="d-flex justify-content-center gap-2">
                                <button
                                  className="btn btn-warning btn-sm"
                                  title="Edit"
                                  onClick={() => openEditModal(review)}
                                >
                                  <i className="fa fa-pencil"></i>
                                  <span className="visually-hidden">Edit</span>
                                </button>

                                <button
                                  className="btn btn-danger btn-sm"
                                  title="Delete"
                                  onClick={() => deleteReview(review.id)}
                                >
                                  <i className="fa fa-trash"></i>
                                  <span className="visually-hidden">
                                    Delete
                                  </span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ================= ADD / EDIT MODAL ================= */}
        {showModal && (
          <div
            onClick={closeModal}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1050,
              padding: "20px",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                width: "100%",
                maxWidth: "600px",
                maxHeight: "80vh",
                overflowY: "auto",
                boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center px-4 py-3"
                style={{
                  borderBottom: "1px solid #eee",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#fff",
                }}
              >
                <div className="">
                  <h5 className="mb-2">
                    {editingId ? "Update Review" : "Add New Review"}
                  </h5>

                  {editingId && (
                    <div className="small text-muted">
                      <div>
                        <strong>Created:</strong>{" "}
                        {selectedReview?.created_at
                          ? new Date(selectedReview.created_at).toLocaleString()
                          : "-"}
                      </div>
                      <div>
                        <strong>Last Updated:</strong>{" "}
                        {selectedReview?.updated_at
                          ? new Date(selectedReview.updated_at).toLocaleString()
                          : "-"}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  className="btn btn-sm"
                  onClick={closeModal}
                  title="Close"
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.2rem",
                  }}
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>

              <div className="p-4" ref={formRef}>
                <div className="mb-3">
                  <label>Reviewer Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.reviewerName ? "is-invalid" : ""}`}
                    value={reviewerName}
                    onChange={(e) => {
                      setReviewerName(e.target.value);
                      clearFieldError("reviewerName");
                    }}
                  />
                  {errors.reviewerName && (
                    <div className="text-danger small mt-1">
                      {errors.reviewerName}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label>Rating</label>
                  <select
                    className={`form-control ${errors.rating ? "is-invalid" : ""}`}
                    value={rating}
                    onChange={(e) => {
                      setRating(e.target.value);
                      clearFieldError("rating");
                    }}
                  >
                    {[5, 4, 3, 2, 1].map((n) => (
                      <option key={n} value={n}>
                        {n} {n === 1 ? "star" : "stars"}
                      </option>
                    ))}
                  </select>
                  {errors.rating && (
                    <div className="text-danger small mt-1">
                      {errors.rating}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label>Review Text</label>
                  <textarea
                    rows="5"
                    maxLength={MAX_REVIEW_LENGTH}
                    className={`form-control ${errors.reviewText ? "is-invalid" : ""}`}
                    value={reviewText}
                    onChange={(e) => {
                      setReviewText(e.target.value);
                      clearFieldError("reviewText");
                    }}
                  />
                  <div
                    className="text-end mt-1"
                    style={{
                      fontSize: "13px",
                      color:
                        reviewText.length >= MAX_REVIEW_LENGTH
                          ? "#dc3545"
                          : "#9aa6b2",
                    }}
                  >
                    {reviewText.length} / {MAX_REVIEW_LENGTH} characters
                  </div>
                  {errors.reviewText && (
                    <div className="text-danger small mt-1">
                      {errors.reviewText}
                    </div>
                  )}
                </div>

                {reviewerImage ? (
                  <div className="text-center mb-3">
                    <img
                      src={URL.createObjectURL(reviewerImage)}
                      alt="Preview"
                      width={220}
                      style={{
                        borderRadius: "10px",
                        maxWidth: "100%",
                      }}
                    />
                  </div>
                ) : (
                  editingId &&
                  currentImage && (
                    <div className="text-center mb-3">
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}php/uploads/reviews/${currentImage}`}
                        alt="Current"
                        width={320}
                        style={{
                          border: "2px dashed #009bd4",
                          padding: "14px",
                          borderRadius: "10px",
                          maxWidth: "100%",
                        }}
                      />
                    </div>
                  )
                )}

                <div className="mb-3">
                  <label>Reviewer Image</label>
                  <input
                    id="reviewerImage"
                    type="file"
                    className={`form-control ${errors.reviewerImage ? "is-invalid" : ""}`}
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={(e) => {
                      if (e.target.files.length > 0) {
                        setReviewerImage(e.target.files[0]);
                        clearFieldError("reviewerImage");
                      }
                    }}
                  />
                  {errors.reviewerImage && (
                    <div className="text-danger small mt-1">
                      {errors.reviewerImage}
                    </div>
                  )}
                </div>

                <div className="d-flex gap-2 justify-content-center">
                  <button
                    className="site-button px-4 py-2"
                    disabled={loading}
                    onClick={editingId ? updateReview : uploadReview}
                  >
                    {loading
                      ? "Uploading..."
                      : editingId
                        ? "Update Review"
                        : "Upload Review"}
                  </button>

                  <button
                    className="site-button bg-secondary px-4 py-2"
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showReviewModal && (
          <div
            onClick={closeReviewModal}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 2000,
              padding: "20px",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                width: "100%",
                maxWidth: "650px",
                borderRadius: "10px",
                overflow: "hidden",
                boxShadow: "0 10px 40px rgba(0,0,0,.25)",
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center px-4 py-3"
                style={{
                  borderBottom: "1px solid #eee",
                }}
              >
                <h5 className="mb-0">Review</h5>

                <button
                  className="btn btn-sm"
                  onClick={closeReviewModal}
                  style={{
                    border: "none",
                    background: "none",
                    fontSize: "1.2rem",
                  }}
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>

              <div
                style={{
                  padding: "24px",
                  maxHeight: "65vh",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  lineHeight: 1.8,
                }}
              >
                {selectedReviewText}
              </div>
            </div>
          </div>
        )}

        <ToastContainer />
      </div>
    </>
  );
};

export default ManageReviews;
